/**
 * StatusReader — public-facing modal for reading a single status update.
 *
 * DEV-1079, Chunk A. Bound to the Summary KPI shape for non-Lead viewers.
 * Open one of these by clicking the Summary KPI dot on the Progress tab,
 * OR by selecting a specific date from the StatusSection's date picker.
 *
 * Layout (from mockups in DEV-1077):
 *   - Header: KPI dot + "Status" + calendar pill + close X
 *   - Body:
 *     - Byline (avatar initials + Lead name + reporting date)
 *     - Narrative HTML (rendered via dangerouslySetInnerHTML — HTML
 *       domain is small and predictable thanks to the minimal TipTap
 *       toolbar; see MiniProjectStatus.cs doc comment for the
 *       no-sanitizer rationale)
 *     - Photo gallery (StatusPhotoStrip in read mode + lightbox)
 *     - Snapshot chip row: Schedule (planned/actual from this status)
 *       and Mission (aggregate of this status's goal assessments).
 *       Budget and Data Reliability aren't versioned per status so
 *       they're intentionally omitted here.
 *
 * Empty state: `status=null` renders the empty-state body (project has
 * no statuses yet) rather than blank chrome.
 *
 * Strings localized via `pages:miniProject.status.reader.*` (Chunk D
 * Phase 1 sweep, 2026-05-22).
 */

import React, { useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import StatusCalendarPicker from "../StatusCalendarPicker/StatusCalendarPicker";
import StatusPhotoStrip, { type StatusPhotoItem } from "../StatusPhotoStrip/StatusPhotoStrip";
import type { MiniProjectStatus } from "../../api/miniProjectStatuses";
import {
  aggregateAssessmentsToColor,
  isQualitativeAssessment,
  type QualitativeAssessment,
} from "../../kpi-engine/qualitativeLadder";
import styles from "./StatusReader.module.css";
import { sanitizeRichText } from "../../utils/sanitizeHtml";

/* ── Types ─────────────────────────────────────────────────────── */

interface Props {
  open: boolean;
  onClose: () => void;
  /** Currently displayed status. null = empty state. */
  status: MiniProjectStatus | null;
  /** All filed reporting dates on this project, for the calendar picker. */
  filedDates: string[];
  /** Fired when the viewer picks a different date from the picker.
   *  Caller refetches the status for that date and passes it back via
   *  the `status` prop. */
  onDateChange: (isoDate: string) => void;
  /** Resolved poster name + initials for the byline. Caller looks these
   *  up from securityUsers and passes them in — keeps this component
   *  free of platform-data dependencies. */
  posterName?: string;
  posterInitials?: string;
}

/* ── Helpers ───────────────────────────────────────────────────── */

function formatLongDate(iso: string, locale: string): string {
  try {
    const [y, m, d] = iso.split("-").map((s) => parseInt(s, 10));
    return new Date(y, (m || 1) - 1, d || 1).toLocaleDateString(
      locale || undefined,
      { year: "numeric", month: "short", day: "numeric" },
    );
  } catch {
    return iso;
  }
}

/** Mission aggregate from a status's per-goal assessments. Color comes
 *  from the shared 5-state ladder helper (qualitativeLadder.ts —
 *  consolidated in DEV-1075 Phase 7.3b from the inline copy this
 *  function previously maintained alongside MissionModal's). Counts
 *  drive the label; the ladder helper drives the color so the two
 *  surfaces (StatusReader snapshot chip + MissionModal aggregate
 *  + Mini-Project KPI dot via Path A) can never drift. */
function aggregateMissionFromAssessments(
  assessments: { assessment: string }[],
  t: (key: string, opts?: Record<string, unknown>) => string,
): { color: "info" | "success" | "warning" | "danger" | "muted"; label: string } {
  if (assessments.length === 0) return { color: "muted", label: t("miniProject.status.reader.mission.noGoals") };

  const valid: QualitativeAssessment[] = assessments
    .map((a) => a.assessment)
    .filter(isQualitativeAssessment);

  const { colorValue, allDone } = aggregateAssessmentsToColor(valid);

  const counts = { ahead: 0, on_track: 0, behind: 0, blocked: 0, done: 0 };
  for (const a of valid) counts[a]++;
  const total = valid.length;
  const inProgress = counts.ahead + counts.on_track;

  if (colorValue === 1) {
    return { color: "danger", label: t("miniProject.status.reader.mission.blocked", { count: counts.blocked }) };
  }
  if (colorValue === 2) {
    return { color: "warning", label: t("miniProject.status.reader.mission.behind", { done: counts.done, behind: counts.behind }) };
  }
  if (colorValue === 3 && allDone) {
    return { color: "info", label: t("miniProject.status.reader.mission.allDone", { total }) };
  }
  if (colorValue === 3) {
    return { color: "success", label: t("miniProject.status.reader.mission.onTrack", { done: counts.done, inProgress }) };
  }
  return { color: "muted", label: t("miniProject.status.reader.mission.noGoals") };
}

/** Schedule color from planned/actual gap. Mirrors the threshold rule
 *  Skip locked: within 5pts green, 5-15 yellow, > 15 or past deadline red.
 *  Takes a t() function so the labels can be localized. */
function classifySchedule(
  plannedPct: number,
  actualPct: number,
  t: (key: string, opts?: Record<string, unknown>) => string,
): { color: "success" | "warning" | "danger"; label: string } {
  const gap = plannedPct - actualPct;
  const abs = Math.abs(gap);
  if (abs <= 5) return { color: "success", label: t("miniProject.status.reader.schedule.onTrack") };
  if (abs <= 15)
    return {
      color: "warning",
      label: gap > 0
        ? t("miniProject.status.reader.schedule.behind", { pts: abs.toFixed(0) })
        : t("miniProject.status.reader.schedule.ahead", { pts: abs.toFixed(0) }),
    };
  return { color: "danger", label: t("miniProject.status.reader.schedule.gap", { pts: abs.toFixed(0) }) };
}

/* ── Component ─────────────────────────────────────────────────── */

const StatusReader: React.FC<Props> = ({
  open,
  onClose,
  status,
  filedDates,
  onDateChange,
  posterName,
  posterInitials,
}) => {
  const { t } = useAppTranslation("pages");

  // Close on Escape (modal pattern).
  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open, onClose]);

  const photos = useMemo<StatusPhotoItem[]>(() => {
    return (status?.photos ?? []).map((p) => ({
      storageKey: p.storageKey,
      caption: p.caption ?? null,
      position: p.position,
    }));
  }, [status]);

  const missionAggregate = useMemo(
    () => aggregateMissionFromAssessments(status?.goalAssessments ?? [], t),
    [status, t],
  );

  const scheduleClass = useMemo(() => {
    if (!status) return null;
    return classifySchedule(Number(status.plannedPct), Number(status.actualPct), t);
  }, [status, t]);

  if (!open) return null;

  // Locale fallback — useAppTranslation could be passed in but for the
  // reader's read-only date formatting we can use browser default.
  const locale = typeof navigator !== "undefined" ? navigator.language : "en";

  const modal = (
    <div
      className={styles.backdrop}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className={styles.modal} role="dialog" aria-modal="true" aria-label={t("miniProject.status.reader.dialogAria")}>
        {/* ── Header ── */}
        <header className={styles.header}>
          <div className={styles.headerLeft}>
            <span className={styles.kpiDot} aria-hidden="true" />
            <span className={styles.title}>{t("miniProject.status.reader.title")}</span>
          </div>
          <div className={styles.headerRight}>
            <StatusCalendarPicker
              value={status?.reportingDate ?? null}
              filedDates={filedDates}
              mode="read"
              onChange={onDateChange}
            />
            <button
              type="button"
              className={styles.closeBtn}
              onClick={onClose}
              aria-label={t("miniProject.status.reader.close")}
            >
              ×
            </button>
          </div>
        </header>

        {/* ── Body ── */}
        <div className={styles.body}>
          {!status ? (
            <div className={styles.emptyState}>
              <p>{t("miniProject.status.reader.empty.title")}</p>
              <p className={styles.emptyHint}>
                {t("miniProject.status.reader.empty.hint")}
              </p>
            </div>
          ) : (
            <>
              <div className={styles.byline}>
                <div className={styles.avatar} aria-hidden="true">
                  {posterInitials || "?"}
                </div>
                <div>
                  <div className={styles.bylineName}>
                    {posterName || status.postedByUserGuid.slice(0, 8) + "…"}
                  </div>
                  <div className={styles.bylineMeta}>
                    {t("miniProject.status.reader.byline.postedLead", { date: formatLongDate(status.reportingDate, locale) })}
                  </div>
                </div>
              </div>

              <div
                className={styles.narrative}
                dangerouslySetInnerHTML={{ __html: sanitizeRichText(status.narrativeHtml) }}
              />

              {photos.length > 0 && (
                <div className={styles.photoSection}>
                  <div className={styles.sectionLabel}>
                    {t("miniProject.status.reader.photos.title")} <span className={styles.muted}>· {photos.length}</span>
                  </div>
                  <StatusPhotoStrip mode="read" photos={photos} />
                </div>
              )}

              {/* Snapshot chip row — only the KPIs actually versioned per
                  status. Budget and Data Reliability aren't versioned so
                  they're not shown here (would mislead with current data
                  instead of point-in-time data). */}
              <div className={styles.snapshotSection}>
                <div className={styles.sectionLabel}>
                  {t("miniProject.status.reader.snapshotTitle", { date: formatLongDate(status.reportingDate, locale) })}
                </div>
                <div className={styles.snapshotRow}>
                  {scheduleClass && (
                    <div className={styles.chip}>
                      <span
                        className={`${styles.chipDot} ${styles[`chipDot_${scheduleClass.color}`]}`}
                        aria-hidden="true"
                      />
                      <div className={styles.chipText}>
                        <div className={styles.chipLabel}>{t("miniProject.status.reader.chip.schedule")}</div>
                        <div className={styles.chipValue}>
                          {t("miniProject.status.reader.chip.scheduleValue", {
                            actual: Number(status.actualPct).toFixed(0),
                            planned: Number(status.plannedPct).toFixed(0),
                          })}
                        </div>
                      </div>
                    </div>
                  )}
                  <div className={styles.chip}>
                    <span
                      className={`${styles.chipDot} ${styles[`chipDot_${missionAggregate.color}`]}`}
                      aria-hidden="true"
                    />
                    <div className={styles.chipText}>
                      <div className={styles.chipLabel}>{t("miniProject.status.reader.chip.mission")}</div>
                      <div className={styles.chipValue}>
                        {t("miniProject.status.reader.chip.missionValue", {
                          count: status.goalAssessments.length,
                          label: missionAggregate.label,
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default StatusReader;
